import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from './PersonaBadge';

export interface LeaderboardEntry {
  id: string;
  name: string;
  value: number;
}

interface MiniLeaderboardProps {
  entries: LeaderboardEntry[];
  limit?: number;
  valueLabel?: string;
  accentColor?: string;
  onEntryClick?: (id: string) => void;
  emptyMessage?: string;
}

const MEDALS = ['🥇', '🥈', '🥉'];

const MiniLeaderboard: React.FC<MiniLeaderboardProps> = ({
  entries,
  limit = 5,
  valueLabel,
  accentColor = 'amber',
  onEntryClick,
  emptyMessage = 'No rankings yet',
}) => {
  const { darkMode } = useTheme();
  const sorted = [...entries].sort((a, b) => b.value - a.value).slice(0, limit);

  // Dynamic border classes don't work with Tailwind purge — use a lookup
  const borderColor: Record<string, string> = {
    amber: darkMode ? 'border-amber-600' : 'border-amber-500',
    purple: darkMode ? 'border-purple-600' : 'border-purple-500',
    indigo: darkMode ? 'border-indigo-600' : 'border-indigo-500',
    blue: darkMode ? 'border-blue-600' : 'border-blue-500',
    cyan: darkMode ? 'border-cyan-600' : 'border-cyan-500',
    emerald: darkMode ? 'border-emerald-600' : 'border-emerald-500',
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-4 ${
    darkMode ? 'bg-gray-800' : 'bg-white shadow-sm'
  } ${borderColor[accentColor] || borderColor.amber}`;

  if (sorted.length === 0) {
    return (
      <div className={sectionClass}>
        <p className={`text-xs text-center py-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className={sectionClass}>
      <div className="space-y-1">
        {sorted.map((entry, i) => {
          const valueColor =
            i === 0 ? 'text-amber-400'
            : i === 1 ? (darkMode ? 'text-gray-300' : 'text-gray-500')
            : i === 2 ? 'text-amber-700'
            : (darkMode ? 'text-gray-400' : 'text-gray-500');

          return (
            <div
              key={entry.id}
              onClick={() => onEntryClick?.(entry.id)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
                onEntryClick ? 'cursor-pointer' : ''
              } ${darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
            >
              <span className="text-sm w-5 text-center shrink-0">
                {MEDALS[i] || (
                  <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>{i + 1}</span>
                )}
              </span>
              <PersonaBadge personaId={entry.id} personaName={entry.name} size="xs" clickable={false} />
              <span className={`text-xs truncate flex-1 min-w-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {entry.name.replace(/_/g, ' ')}
              </span>
              <span className={`text-sm font-bold shrink-0 tabular-nums ${valueColor}`}>
                {entry.value}
              </span>
            </div>
          );
        })}
      </div>
      {valueLabel && (
        <p className={`text-[10px] text-right mt-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          ranked by {valueLabel}
        </p>
      )}
    </div>
  );
};

export default MiniLeaderboard;
